import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';

/** Arrangement `Hero_Coffee_Spotlight` — Block=Hero; Chrome N/A. */
export function HeroCoffeeSpotlight({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'browse',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const badgeText = pickBlockField(block.content, 'badgeText', locale);
  const heroImage = pickMediaUrl(block, 'heroImage');
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 960, 78) : undefined;
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [480, 720, 960], 78)
    : undefined;
  const imageAlt = heading || eyebrow || 'Signature';
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-hero-pedestal', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="top"
    >
      <div className="ws-hero-pedestal__product">
        <div className="ws-hero-pedestal__disk" aria-hidden />
        {heroImage ? (
          <EditableMedia
            fieldKey="heroImage"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-pedestal__photo"
            src={heroImageSrc}
            srcSet={heroImageSrcSet}
            sizes={SITE_IMAGE_SIZES.heroPedestal}
            alt={imageAlt}
            width={720}
            height={720}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        ) : (
          <div className="ws-hero-pedestal__placeholder" aria-hidden />
        )}
      </div>
      <div id="about" className="ws-hero-pedestal__card">
        {badgeText ? (
          <EditableContent
            fieldKey="badgeText"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-pedestal__badge"
          >
            {badgeText}
          </EditableContent>
        ) : null}
        <EditableContent
          fieldKey="eyebrow"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-pedestal__eyebrow"
        >
          {eyebrow}
        </EditableContent>
        <EditableContent
          as={Heading}
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-pedestal__title"
        >
          {heading}
        </EditableContent>
        <EditableContent
          fieldKey="subheading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-pedestal__lead"
        >
          {subheading}
        </EditableContent>
        {ctaPrimary ? (
          <EditableContent
            as="a"
            fieldKey="ctaPrimary"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-pedestal__cta"
            href={ctaPrimaryHref}
          >
            {ctaPrimary}
          </EditableContent>
        ) : null}
      </div>
    </section>
  );
}
